<script setup lang="ts">
import stores from '@delivery/stores';
import { CallMode, Language } from '@shared/openapi/delivery-api';
import { ref } from 'vue';

const { status, items, error } = stores.calls.state;
const { startCall } = stores.calls.funcs;

const mode = ref<CallMode>(CallMode.BROWSER);
const phoneNumber = ref('+4915123378181');
const language = ref<Language>(Language.EN);
const isSubmitting = ref(false);

const languageOptions = [
  { title: 'English', value: Language.EN },
  { title: 'German', value: Language.DE }
];

const isDone = () => status.value === 'completed' || status.value === 'failed';
const isLocked = () => isSubmitting.value || (!!status.value && !isDone());

const statusLabel = () => {
  if (!status.value) return '';
  return {
    dialing: mode.value === CallMode.BROWSER ? 'Connecting…' : 'Dialing…',
    in_progress: 'Call in progress…',
    completed: 'Call finished.',
    failed: 'The call could not be completed.'
  }[status.value];
};

const onSubmit = async () => {
  isSubmitting.value = true;
  try {
    await startCall(
      mode.value,
      mode.value === CallMode.PHONE ? phoneNumber.value : undefined,
      language.value
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <v-container class="fill-height d-flex align-center justify-center">
    <v-card max-width="480" width="100%" class="pa-6">
      <v-card-title>Order groceries by phone</v-card-title>
      <v-card-subtitle>
        {{
          mode === CallMode.PHONE
            ? "We'll call this number and ask what supermarket products to order."
            : "We'll connect you to the ordering agent right here in your browser."
        }}
      </v-card-subtitle>

      <v-card-text>
        <v-radio-group v-model="mode" inline density="compact" :disabled="isLocked()">
          <v-radio label="Browser" :value="CallMode.BROWSER" />
          <v-radio label="Phone" :value="CallMode.PHONE" />
        </v-radio-group>

        <v-text-field
          v-if="mode === CallMode.PHONE"
          v-model="phoneNumber"
          label="Phone number"
          placeholder="+491701234567"
          hint="E.164 format, e.g. +491701234567"
          persistent-hint
          :disabled="isLocked()"
        />

        <v-select
          v-model="language"
          :items="languageOptions"
          item-title="title"
          item-value="value"
          label="Language"
          :disabled="isLocked()"
        />

        <v-btn
          class="mt-4"
          color="primary"
          block
          :loading="isSubmitting"
          :disabled="(mode === CallMode.PHONE && !phoneNumber) || isLocked()"
          @click="onSubmit"
        >
          {{ mode === CallMode.PHONE ? 'Call & order groceries' : 'Call & order groceries' }}
        </v-btn>

        <v-alert v-if="error" type="error" class="mt-4" density="compact">
          {{ error }}
        </v-alert>

        <div v-if="status" class="mt-6">
          <div class="text-subtitle-1">{{ statusLabel() }}</div>

          <v-progress-circular
            v-if="!isDone()"
            indeterminate
            color="primary"
            class="mt-4"
          />

          <v-list v-if="status === 'completed'" class="mt-2">
            <v-list-item v-if="items.length === 0">
              <v-list-item-title>No products were ordered.</v-list-item-title>
            </v-list-item>
            <v-list-item v-for="(item, index) in items" :key="index">
              <v-list-item-title
                >{{ item.quantity }} {{ item.unit }} of {{ item.product }}</v-list-item-title
              >
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>
